import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import * as bookingService from "../../modules/booking/booking.service";
import {
  ICartDetail,
  IAddToCartPayload,
  IUpdateCartItemPayload,
} from "../../modules/booking/bookingModel";
import {
  getProductUnitPrice,
  getCartItemEffectivePricing,
  IProductPriceTier,
} from "@/utils/priceUtils";
import { isInternationalUserSync } from "@/utils/location";

export interface CartItem {
  ProductID: number;
  ProductName: string;
  RawBasePrice?: number;
  BasePrice?: number;
  Price: number;
  Qty: number;
  TotalPrice: number;
  URL?: string;
  CartID?: number; // Optional, present when synced with backend
  MainImage?: string;
  GSTPer?: number;
  GSTValue?: number;
  MinimumQty?: number | null;
  IsCostToCost?: boolean;
  AvailableQty?: number;
  RawProductPrices?: IProductPriceTier[];
  ProductPrices?: IProductPriceTier[];
}

interface CartState {
  items: CartItem[];
  loading: boolean;
  error: string | null;
  isSyncing: boolean;
  isCartDrawerOpen: boolean;
}

const initialState: CartState = {
  items: [],
  loading: false,
  error: null,
  isSyncing: false,
  isCartDrawerOpen: false,
};

// Async Thunks
export const fetchCartAction = createAsyncThunk(
  "cart/fetchCart",
  async (customerID: number) => {
    const response = await bookingService.getCartDetails(customerID);
    return response.Data || [];
  },
);

export const addToCartAction = createAsyncThunk(
  "cart/addToCart",
  async (payload: IAddToCartPayload) => {
    // Filter out items with 0 quantity
    const filteredPayload = {
      ...payload,
      CartItems: payload.CartItems.filter((item) => item.Qty > 0),
    };

    if (filteredPayload.CartItems.length > 0) {
      await bookingService.addToCart(filteredPayload);
    }
    const response = await bookingService.getCartDetails(payload.CustomerID);
    return response.Data || [];
  },
);

export const updateCartQtyAction = createAsyncThunk(
  "cart/updateQty",
  async (payload: IUpdateCartItemPayload) => {
    if (payload.Qty <= 0) {
      // Logic for removing if qty is 0 could be here or separate
    }
    await bookingService.updateCartItem(payload);
    const response = await bookingService.getCartDetails(payload.CustomerID);
    return response.Data || [];
  },
);

export const syncCartOnLoginAction = createAsyncThunk(
  "cart/syncOnLogin",
  async ({
    customerID,
    localItems,
  }: {
    customerID: number;
    localItems: CartItem[];
  }) => {
    // 1. Fetch current backend cart
    const backendResponse = await bookingService.getCartDetails(customerID);
    const backendItems = backendResponse.Data || [];

    // 2. Build merge plan
    // For each local item, check if it exists in backend
    for (const localItem of localItems) {
      // Safeguard: If item already has a CartID, it was already synced in a previous pass
      // and we should trust the backend's current quantity rather than merging again.
      if (localItem.CartID) continue;

      const backendMatch = backendItems.find(
        (bi) => bi.ProductID === localItem.ProductID,
      );

      if (backendMatch) {
        // If in backend, merge qty (sum them up or take local intent? User said "merge qty")
        // Usually merging means summing if they was added separately,
        // but if we want to avoid double counting from a previous sync, we need to be careful.
        // The requirement "Same ProductID -> merge qty" suggests summing.
        await bookingService.updateCartItem({
          CartID: backendMatch.CartID,
          CustomerID: customerID,
          ProductID: localItem.ProductID,
          Qty: backendMatch.Qty + localItem.Qty,
          Price: localItem.Price,
          GSTPer: localItem.GSTPer,
        });
      } else if (localItem.Qty > 0) {
        // If not in backend and qty > 0, add it
        await bookingService.addToCart({
          CustomerID: customerID,
          CartItems: [
            {
              ProductID: localItem.ProductID,
              Qty: localItem.Qty,
              Price: localItem.Price,
              GSTPer: localItem.GSTPer,
            },
          ],
        });
      }
    }

    // 3. Final fetch to get the unified, merged cart
    const finalResponse = await bookingService.getCartDetails(customerID);
    return finalResponse.Data || [];
  },
);

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    syncCartFromLocal: (state, action: PayloadAction<CartItem[]>) => {
      state.items = action.payload;
      state.loading = false;
    },
    setCartDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isCartDrawerOpen = action.payload;
    },
    toggleCartDrawer: (state) => {
      state.isCartDrawerOpen = !state.isCartDrawerOpen;
    },
    addItem: (state, action: PayloadAction<CartItem>) => {
      const item = { ...action.payload };
      if (!item.ProductID) return;

      const existingItem = state.items.find(
        (i) => i.ProductID === item.ProductID,
      );
      const availableQty =
        item.AvailableQty !== undefined
          ? item.AvailableQty
          : existingItem?.AvailableQty;

      const isIntl = isInternationalUserSync();
      const rawBasePrice =
        item.RawBasePrice || existingItem?.RawBasePrice;
      const rawProductPrices =
        item.RawProductPrices || existingItem?.RawProductPrices;

      const { basePrice, productPrices } = getCartItemEffectivePricing(
        {
          RawBasePrice: rawBasePrice,
          BasePrice: item.BasePrice || existingItem?.BasePrice,
          Price: item.Price,
          RawProductPrices: rawProductPrices,
          ProductPrices: item.ProductPrices || existingItem?.ProductPrices,
        },
        isIntl,
      );

      if (existingItem) {
        let newQty = existingItem.Qty + item.Qty;
        if (availableQty !== undefined && availableQty !== null) {
          newQty = Math.min(availableQty, newQty);
        }
        existingItem.Qty = newQty;
        if (availableQty !== undefined) {
          existingItem.AvailableQty = availableQty;
        }

        const unitPrice = getProductUnitPrice(
          basePrice,
          productPrices,
          existingItem.Qty,
        );
        existingItem.RawBasePrice = rawBasePrice;
        existingItem.RawProductPrices = rawProductPrices;
        existingItem.BasePrice = basePrice;
        existingItem.ProductPrices = productPrices;
        existingItem.Price = unitPrice;
        existingItem.TotalPrice = existingItem.Qty * unitPrice;
        // Update GSTPer if it's missing on existing item but provided in new item
        if (!existingItem.GSTPer && item.GSTPer) {
          existingItem.GSTPer = item.GSTPer;
        }
      } else {
        if (availableQty !== undefined && availableQty !== null) {
          item.Qty = Math.min(availableQty, item.Qty);
        }
        const unitPrice = getProductUnitPrice(
          basePrice,
          productPrices,
          item.Qty,
        );
        item.RawBasePrice = rawBasePrice;
        item.RawProductPrices = rawProductPrices;
        item.BasePrice = basePrice;
        item.ProductPrices = productPrices;
        item.Price = unitPrice;
        item.TotalPrice = item.Qty * unitPrice;
        state.items.push(item);
      }

      state.isCartDrawerOpen = true;

      if (typeof window !== "undefined") {
        localStorage.setItem("nakman_cart", JSON.stringify(state.items));
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(
        (item) => item.ProductID !== action.payload,
      );
      if (typeof window !== "undefined") {
        localStorage.setItem("nakman_cart", JSON.stringify(state.items));
      }
    },
    updateQty: (
      state,
      action: PayloadAction<{ ProductID: number; Qty: number }>,
    ) => {
      const { ProductID, Qty } = action.payload;
      const item = state.items.find((item) => item.ProductID === ProductID);
      if (item) {
        const minQty = item.MinimumQty || 1;
        let newQty = Math.max(minQty, Qty);
        if (item.AvailableQty !== undefined && item.AvailableQty !== null) {
          newQty = Math.min(item.AvailableQty, newQty);
        }
        item.Qty = newQty;
        const isIntl = isInternationalUserSync();
        const { basePrice, productPrices } = getCartItemEffectivePricing(
          item,
          isIntl,
        );
        const unitPrice = getProductUnitPrice(
          basePrice,
          productPrices,
          item.Qty,
        );
        item.BasePrice = basePrice;
        item.ProductPrices = productPrices;
        item.Price = unitPrice;
        item.TotalPrice = item.Qty * unitPrice;
      }
      if (typeof window !== "undefined") {
        localStorage.setItem("nakman_cart", JSON.stringify(state.items));
      }
    },
    clearCart: (state) => {
      state.items = [];
      if (typeof window !== "undefined") {
        localStorage.removeItem("nakman_cart");
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Cart
    builder.addCase(fetchCartAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCartAction.fulfilled, (state, action) => {
      state.loading = false;
      const existingItems = [...state.items];
      state.items = action.payload.map((item: ICartDetail) => 
        mapBackendItemToLocal(item, existingItems)
      );
      if (typeof window !== "undefined") {
        localStorage.setItem("nakman_cart", JSON.stringify(state.items));
      }
    });
    builder.addCase(fetchCartAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch cart";
    });

    // Add To Cart
    builder.addCase(addToCartAction.pending, (state) => {
      state.isSyncing = true;
    });
    builder.addCase(addToCartAction.fulfilled, (state, action) => {
      state.isSyncing = false;
      const existingItems = [...state.items];
      state.items = action.payload.map((item: ICartDetail) =>
        mapBackendItemToLocal(item, existingItems)
      );

      state.isCartDrawerOpen = true;

      if (typeof window !== "undefined") {
        localStorage.setItem("nakman_cart", JSON.stringify(state.items));
      }
    });

    // Update Qty
    builder.addCase(updateCartQtyAction.fulfilled, (state, action) => {
      const existingItems = [...state.items];
      state.items = action.payload.map((item: ICartDetail) =>
        mapBackendItemToLocal(item, existingItems)
      );
      if (typeof window !== "undefined") {
        localStorage.setItem("nakman_cart", JSON.stringify(state.items));
      }
    });

    // Sync On Login
    builder.addCase(syncCartOnLoginAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(syncCartOnLoginAction.fulfilled, (state, action) => {
      state.loading = false;
      const existingItems = [...state.items];
      state.items = action.payload.map((item: ICartDetail) => 
        mapBackendItemToLocal(item, existingItems)
      );
      if (typeof window !== "undefined") {
        localStorage.setItem("nakman_cart", JSON.stringify(state.items));
      }
    });

    // Global Logout
    builder.addCase("auth/logoutAll", (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
      if (typeof window !== "undefined") {
        localStorage.removeItem("nakman_cart");
      }
    });
  },
});

const mapBackendItemToLocal = (item: ICartDetail, existingItems: CartItem[] = []): CartItem => {
  const existingItem = existingItems.find((i) => i.ProductID === item.ProductID);
  const isIntl = isInternationalUserSync();

  const rawBasePrice = existingItem?.RawBasePrice || item.Price;
  const rawProductPrices = existingItem?.RawProductPrices || item.ProductPrices;

  const { basePrice, productPrices } = getCartItemEffectivePricing(
    {
      RawBasePrice: rawBasePrice,
      BasePrice: existingItem?.BasePrice || item.Price,
      Price: item.Price,
      RawProductPrices: rawProductPrices,
      ProductPrices: existingItem?.ProductPrices || item.ProductPrices,
    },
    isIntl,
  );
  const unitPrice = getProductUnitPrice(basePrice, productPrices, item.Qty);

  return {
    ProductID: item.ProductID,
    ProductName: item.ProductName,
    RawBasePrice: rawBasePrice,
    RawProductPrices: rawProductPrices,
    BasePrice: basePrice,
    Price: unitPrice,
    Qty: item.Qty,
    TotalPrice: item.Qty * unitPrice,
    URL: item.URL,
    CartID: item.CartID,
    MainImage: item.MainImage,
    GSTPer: item.GSTPer || existingItem?.GSTPer,
    MinimumQty: item.MinimumQty || existingItem?.MinimumQty,
    IsCostToCost: item.IsCostToCost || existingItem?.IsCostToCost,
    AvailableQty:
      item.ProductQty !== undefined && item.ProductQty !== null
        ? item.ProductQty
        : existingItem?.AvailableQty,
    ProductPrices: productPrices,
  };
};

export const { syncCartFromLocal, addItem, removeItem, updateQty, clearCart, setCartDrawerOpen, toggleCartDrawer } =
  cartSlice.actions;

export default cartSlice.reducer;
